import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const COMPONENTS = [["customs", "Customs Duty"], ["aidc", "Agriculture Infrastructure & Development Cess (AIDC)"], ["gst", "GST"]];
const inr = (v) => (v == null ? "—" : `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`);

const Preview = ({ pv }) => {
  if (!pv) return null;
  const r = pv.result;
  return (
    <div className="rounded-2xl bg-stone-50 border border-stone-200 p-4 text-xs space-y-1" data-testid="gold-settings-preview">
      <div className="font-black text-stone-900">Live calculation preview · {pv.status}</div>
      <div>International spot: ${pv.xau_usd_per_oz ?? "—"}/oz · USD/INR {pv.usd_inr ?? "—"} · {pv.source} · {pv.source_ts ? new Date(pv.source_ts).toLocaleString() : "—"}</div>
      <div>Spot ₹/g: {inr(r?.international_inr_per_gram)}</div>
      {pv.calculation?.components?.map((c) => (
        <div key={c.name}>{c.name}: {c.pct}% · {c.enabled ? (c.applied ? "applied" : "enabled, shown separately (excluded from total)") : "disabled"}</div>
      ))}
      <div>Method: {pv.calculation?.method} · multiplier × {pv.calculation?.multiplier}</div>
      <div className="font-black text-stone-900" data-testid="gold-preview-24k">24K estimate: {inr(r?.gold_24k_per_gram)}/g · {inr(r?.gold_24k_per_10g)}/10 g</div>
      <div>22K {inr(r?.gold_22k_per_gram)}/g · 18K {inr(r?.gold_18k_per_gram)}/g · excl. GST {inr(r?.gold_24k_excl_gst_per_gram)} · incl. GST {inr(r?.gold_24k_incl_gst_per_gram)}</div>
      <div className="text-stone-500">{pv.notice}</div>
    </div>
  );
};

export const GoldSettingsPanel = () => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [pv, setPv] = useState(null);
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const load = useCallback(async () => {
    const r = await api.get("/admin/gold-settings");
    setData(r.data); setForm(r.data.settings); setPv(r.data.preview); setConfirm(false);
  }, []);
  const [loadErr, setLoadErr] = useState(null);
  const retry = useCallback(() => {
    setLoadErr(null);
    load().catch((e) => {
      const msg = e.response?.status === 403 ? "Admin access required" : (e.response?.data?.detail || "Could not load market-rate settings");
      setLoadErr(String(msg)); toast.error(String(msg));
    });
  }, [load]);
  useEffect(() => { retry(); }, [retry]);
  if (loadErr) return (
    <div className="text-sm text-red-700 flex items-center gap-3" data-testid="gold-settings-error">
      {loadErr}
      <button onClick={retry} className="font-bold underline" data-testid="gold-settings-retry">Retry</button>
    </div>
  );
  if (!form) return <div className="text-sm text-stone-500" data-testid="gold-settings-loading">Loading…</div>;

  const patch = () => {
    const out = {};
    ["customs_pct", "aidc_pct", "gst_pct"].forEach((k) => { out[k] = Number(form[k]); });
    ["customs_enabled", "aidc_enabled", "gst_enabled"].forEach((k) => { out[k] = !!form[k]; });
    out.method = form.method; out.display_mode = form.display_mode;
    return out;
  };
  const err = (e) => toast.error(e.response?.data?.detail || "Failed");
  const preview = () => api.post("/admin/gold-settings/preview", { patch: patch() }).then((r) => setPv(r.data.preview)).catch(err);
  const publish = () => api.put("/admin/gold-settings", { patch: patch(), reason, confirm, expected_version: data.settings.version })
    .then(() => { toast.success("Settings published"); setReason(""); load(); }).catch(err);
  const rollback = () => api.post("/admin/gold-settings/rollback", { reason, confirm, expected_version: data.settings.version })
    .then(() => { toast.success("Rolled back"); setReason(""); load(); }).catch(err);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const box = "px-3 py-2 rounded-xl border border-stone-200 text-sm";

  return (
    <div className="space-y-4" data-testid="gold-settings-panel">
      <div>
        <h2 className="font-black text-lg text-stone-900">Market Rates · Gold Price Calculation Settings</h2>
        <p className="text-xs text-amber-700 font-bold" data-testid="gold-settings-label">{data.label} · version {data.settings.version} · purity reference {form.purity_ref}</p>
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        {COMPONENTS.map(([k, label]) => (
          <div key={k} className="rounded-2xl border border-stone-200 p-3">
            <label className="flex items-center justify-between text-xs font-bold text-stone-700">{label}
              <input type="checkbox" checked={!!form[`${k}_enabled`]} onChange={(e) => set(`${k}_enabled`, e.target.checked)} data-testid={`gold-${k}-enabled`} />
            </label>
            <input type="number" min="0" max={data.max_pct} step="0.01" value={form[`${k}_pct`]} onChange={(e) => set(`${k}_pct`, e.target.value)}
              className={`${box} mt-2 w-full`} data-testid={`gold-${k}-pct`} />
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-3">
        <select value={form.method} onChange={(e) => set("method", e.target.value)} className={box} data-testid="gold-method">
          <option value="combined">Combined (1 + Σ rates)</option><option value="sequential">Sequential (Π (1 + rate))</option>
        </select>
        <select value={form.display_mode} onChange={(e) => set("display_mode", e.target.value)} className={box} data-testid="gold-display-mode">
          <option value="excl_gst">Display excluding GST</option><option value="incl_gst">Display including GST</option>
        </select>
        <button onClick={preview} className="px-4 py-2 rounded-full bg-stone-900 text-white text-xs font-black" data-testid="gold-preview-btn">Preview</button>
      </div>
      <Preview pv={pv} />
      <div className="rounded-2xl border border-stone-200 p-3 space-y-2">
        <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for this change (required)" className={`${box} w-full`} data-testid="gold-reason" />
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="gold-confirm" />I reviewed the preview and want to publish to all price displays</label>
        <div className="flex gap-2">
          <button onClick={publish} className="px-4 py-2 rounded-full bg-[#2D5A27] text-white text-xs font-black" data-testid="gold-publish-btn">Publish</button>
          <button onClick={rollback} className="px-4 py-2 rounded-full bg-stone-100 text-stone-800 text-xs font-black" data-testid="gold-rollback-btn">Roll back last change</button>
        </div>
      </div>
      <div className="text-xs" data-testid="gold-settings-history">
        <div className="font-black text-stone-900 mb-1">Change history</div>
        {(data.history || []).map((h) => (
          <div key={h.audit_id} className="text-stone-600">v{h.prev_version}→v{h.new_version} · {h.action} · {new Date(h.at).toLocaleString()} · {h.admin_id} · “{h.reason}”</div>
        ))}
      </div>
    </div>
  );
};
